(ns admin.components.composite.questionnaire.question-card
  (:require
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]

   [admin.components.composite.markup :as markup]
   [admin.components.composite.learning-objective-pill :as lo-pill]
   [admin.components.composite.question-preview :as question-preview]
   [kyber.constants :refer [partner-color]]
   [kyber.components.base.icons :as icon]
   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.styles.core :refer [gs styles->classes]]))

(def collapsed-question-container-height 130)

(def classes
  (styles->classes
   {:info-line
    {:color (gs [:colors :gray-scale-400])}

    :question-card-container
    {
     :border-radius (gs [:radius :r3])
     :box-shadow (gs [:shadows :shadow-6])
     :width "100%"
     :max-width "1000px"
     :padding (gs [:spacing :p20])
     :padding-bottom (gs [:spacing :p8])
     :position :relative
     :background-color (gs [:colors :true-white])
     :z-index 1
     :transition (str "border 0.2s ease-in-out, "
                      "box-shadow 0.2s ease-in-out")

     "& > .overlay"
     {:height "100%"
      :width "100%"
      :top 0
      :left 0
      :z-index 1
      :position :absolute
      :background-color "rgba(255,255,255,0.5)"}}

    :not-key-question
    {:border (str "1px solid " (gs [:colors :gray-scale-50]))}

    :is-hovered-question-card-container
    {:transform "rotate(-1deg) !important"
     :border (str "1px solid " (gs [:colors :gray-scale-500])
                  " !important")
     :box-shadow (str (gs [:shadows :shadow-24]) "!important")}

    :scaled-question-card-container
    {:width "calc(50% - 20px) !important"}

    ;; Should only apply to top level questionnares to center the cards.
    ;; centering assesment types will break the pre/post test navigation
    :top-level-questionnaire-container
    {:margin :auto}

    :question-preview-container
    {:display :flex
     :justify-content :space-between
     :transition "max-height 0.5s ease-in-out"
     :width "100%"
     :max-height "1000px" ;; NOTE a bit hacky but supports animations
     :min-height (str collapsed-question-container-height "px")
     :overflow :hidden}

    :question-preview-collapsed-container
    {:max-height (str collapsed-question-container-height "px !important")
     :height :auto
     :position :relative}

    :question-preview-collapsed-overflow-overlay
    {:position :absolute
     :bottom 0
     :width "100%"
     :height "30px"
     :background (str "linear-gradient(to bottom, "
                      "rgba(255,255,255,0), "
                      "rgba(255,255,255,0.8))")}

    :question-number
    {:margin-right (gs [:spacing :p20])
     :padding-right (gs [:spacing :p20])
     :border-right (str "1px solid " (gs [:colors :gray-scale-200]))}

    :question-title-container
    {:display :flex
     :align-items :center}

    :question-type
    {:margin-right (gs [:spacing :p8])}

    :question-check-container
    {:padding-left (gs [:spacing :p8])
     :margin-left (gs [:spacing :p8])
     :border-left (str "1px solid " (gs [:colors :gray-scale-200]))}

    :expand-collapse-link
    {}

    :key-question-container
    {:border-top (str "10px solid " partner-color)}

    :cta-container
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :border-top (str "1px solid " (gs [:colors :gray-scale-100]))
     :padding-top (gs [:spacing :p8])
     :margin-top (gs [:spacing :p8])}

    :remove-and-update-cta-container
    {:display :flex
     :overflow "hidden"
     :align-items :center}

    :grab-bar-icon
    {:cursor :grab
     :padding (gs [:spacing :p4])
     :margin-right (gs [:spacing :p8])
     :color (gs [:colors :gray-scale-400])}}))

(def question-type
  {:multiple-answer "Multiple Answer"
   :single-answer "Single Answer"
   :free-response "Free Response"
   :rating-scale "Rating Scale"})

(def question-type->title
  #(get question-type (keyword %)))

(defn- render-question-check
  [{:keys [message complete]}]
  [:div {:style {:display :flex
                 :align-items :center}}
   (if (true? complete)
     [icon/icon-sm
      {:style {:color (gs [:colors :chart-green])}}
      :check]
     [icon/icon-sm
      {:style {:color (gs [:colors :chart-orange])}}
      :warning-triangle])
   [text/caption-10 :p
    {:style {:margin-left (gs [:spacing :p4])}}
    message]])

(defn- render-info-line
  [{:keys [confidence required]}]
  (cond
    (and (true? confidence)
         (true? required))
    [text/caption-10 :p
     {:class (:info-line classes)}
     "(Required with Confidence)"]

    (true? confidence)
    [text/caption-10 :p
     {:class (:info-line classes)}
     "(Confidence)"]

    (true? required)
    [text/caption-10 :p
     {:class (:info-line classes)}
     "(Required)"]))

(defn- get-element-idx
  [element]
  (when (some? element)
    (let [data-idx (.getAttribute element "data-idx")]
      (if (some? data-idx)
        (js/parseInt data-idx)
        (get-element-idx (.-parentNode element))))))

(defn- set-hover-idx
  [{:keys [hover-event activity-uuid questionnaire-type questionnaire-uuid element-idx hover-idx]}]
  (when-not (= @hover-idx element-idx)
    (re-frame/dispatch [hover-event
                        {:activity-uuid activity-uuid
                         :questionnaire-type questionnaire-type
                         :questionnaire-uuid questionnaire-uuid
                         :question-idx element-idx}])
    (reset! hover-idx element-idx)))

(defn render
  [_]
  (let [is-expanded (reagent/atom false)
        hover-idx (reagent/atom nil)
        is-dragging (atom false)]
    (fn [{:keys [idx
                 is-hovering
                 activity-uuid
                 question
                 resources
                 confidence
                 required
                 learning-objective
                 learning-objective-label
                 hide-recommendations
                 scale-card
                 questionnaire-is-updating
                 questionnaire-api
                 questionnaire-type
                 on-remove-question
                 on-edit-question
                 questionnaire-uuid]
         :or {scale-card false}}]
      (let [{question-body :question/body
             question-type :question/type
             educator-feedback :question/educator-feedback
             key-question :question/key?} question
            
            question-hover-idx-event
            (:question-hover-idx-event questionnaire-api)

            clear-drag-state-event
            (:question-clear-drag-event questionnaire-api)
            
            update-question-ctx-sort-order
            (:questionnaire-sort-event questionnaire-api)]
        [:div {:data-idx
               idx

               :draggable
               true

               :on-drag-start
               #(do
                  (reset! is-dragging true)
                  (.setData (.-dataTransfer %) "text" idx))

               :on-drag-enter
               #(let [element-idx (get-element-idx (.-target %))]
                  (.preventDefault %)
                  (set-hover-idx {:hover-event question-hover-idx-event
                                  :activity-uuid activity-uuid
                                  :questionnaire-type questionnaire-type
                                  :questionnaire-uuid questionnaire-uuid
                                  :hover-idx hover-idx
                                  :element-idx element-idx}))

               :on-drag-leave
               #(do
                  (.preventDefault %)
                  (set-hover-idx {:hover-event question-hover-idx-event
                                  :activity-uuid activity-uuid
                                  :questionnaire-type questionnaire-type
                                  :questionnaire-uuid questionnaire-uuid
                                  :hover-idx hover-idx
                                  :element-idx nil}))

               :on-drag-over
               #(let [element-idx (get-element-idx (.-target %))]
                  (.preventDefault %)
                  (set-hover-idx {:hover-event question-hover-idx-event
                                  :activity-uuid activity-uuid
                                  :questionnaire-type questionnaire-type
                                  :questionnaire-uuid questionnaire-uuid
                                  :hover-idx hover-idx
                                  :element-idx element-idx}))

               :on-drop
               #(let [end-idx (get-element-idx (.-target %))
                      start-idx (js/parseInt (.getData (.-dataTransfer %)
                                                       "text"))]
                  (.preventDefault %)
                  (reset! is-dragging false)
                  (re-frame/dispatch [clear-drag-state-event
                                      {:activity-uuid activity-uuid
                                       :questionnaire-type questionnaire-type
                                       :questionnaire-uuid questionnaire-uuid}])

                  (when (and (some? end-idx)
                             (some? start-idx)
                             (not= end-idx start-idx))
                    (re-frame/dispatch
                     [update-question-ctx-sort-order
                      {:questionnaire-type questionnaire-type
                       :questionnaire-uuid questionnaire-uuid
                       :question-start-idx (js/parseInt start-idx)
                       :question-end-idx (js/parseInt end-idx)
                       :activity-uuid activity-uuid}])))

               :on-drag-end
               #(do
                  (.preventDefault %)
                  (reset! is-dragging false)
                  (re-frame/dispatch
                   [clear-drag-state-event
                    {:activity-uuid activity-uuid
                     :questionnaire-type questionnaire-type
                     :questionnaire-uuid questionnaire-uuid}]))

               :class
               (cond-> [(:question-card-container classes)]
                 (true? key-question)
                 (conj (:key-question-container classes))
                 (false? key-question)
                 (conj (:not-key-question classes))
                 (true? scale-card)
                 (conj (:scaled-question-card-container classes))

                 (true? (= questionnaire-type :floating))
                 (conj (:top-level-questionnaire-container classes))

                 (and (false? @is-dragging)
                      (true? is-hovering))
                 (conj (:is-hovered-question-card-container classes)))}

         (when (true? @is-dragging)
           [:div {:class "overlay"}])

         [:div {:class
                (cond-> [(:question-preview-container classes)]
                  (false? @is-expanded)
                  (conj (:question-preview-collapsed-container classes)))}

          [:div {:style {:display :flex
                         :width "80%"}}
           [icon/icon-lg {:class (:grab-bar-icon classes)}
            :grip-vertical]
           [text/title-30 :p {:class (:question-number classes)} (inc idx)]

           [:div {:style {:width "100%" :position "relative"}}
            [:div {:class (:question-title-container classes)}
             [text/caption-30 :p
              {:class (:question-type classes)}
              (question-type->title question-type)]

             [render-info-line {:required required
                                :confidence confidence}]]
            [lo-pill/render
             {:label learning-objective-label}]
            [markup/render question-body]

            (when (false? @is-expanded)
              [:div
               {:class (:question-preview-collapsed-overflow-overlay classes)}])

            (when (true? @is-expanded)
              [question-preview/render {:question question}])]]

          (when-not (true? hide-recommendations)
            [:div {:class (:question-check-container classes)}
             [render-question-check
              {:complete (some? educator-feedback)
               :message "Contains educator feedback"}]

             [render-question-check
              {:complete (pos? (count resources))
               :message "Contains at least 1 resource"}]

             [render-question-check
              {:complete (some? learning-objective)
               :message "Has an assigned learning objective"}]])]

         [:div {:class (:cta-container classes)}
          [:div
           (when-not (= question-type "free-response")
             (if (true? @is-expanded)
               [button/button-link
                {:on-click #(reset! is-expanded false)
                 :class (:expand-collapse-link classes)}
                "Hide question"]
               [button/button-link
                {:on-click #(reset! is-expanded true)
                 :class (:expand-collapse-link classes)}
                "Show full question"]))]
          [:div {:class (:remove-and-update-cta-container classes)}
           [button/primary-button
            {:on-click on-edit-question
             :disabled questionnaire-is-updating}
            "Update Question"]

           [button/admin-button-link
            {:style {:margin-left (gs [:spacing :p20])}
             :disabled questionnaire-is-updating
             :on-click #(re-frame/dispatch
                         [:notifier/create-alert
                          {:title "Remove Question?"
                           :message (str "Are you sure you want to remove this "
                                         "question?")
                           :confirm-text "Remove Question"
                           :confirm-action on-remove-question}])}
            "Remove Question"]]]]))))
